/**
 * Calculates the bounding box for a set of points
 * @param {Array<number>} pts - Array of coordinates [x1, y1, x2, y2, ...]
 * @returns {Object} Object containing minX, minY, width, and height
 */
export const calculateBounds = (pts) => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (let i = 0; i < pts.length; i += 2) {
    const x = pts[i];
    const y = pts[i + 1];
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  return {
    minX,
    minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Normalizes arrow coordinates to ensure consistent positioning
 * @param {Array<Object>} arrows - Array of arrow objects
 * @returns {Array<Object>} Array of normalized arrow objects
 */
export const normalizeArrowCoordinates = (arrows) =>
  arrows.map((arrow) => {
    const { points = [0, 0, 0, 0], x = 0, y = 0 } = arrow;

    // Always treat stored points as RELATIVE to (x, y)
    const absolutePoints = points.map((coord, i) =>
      i % 2 === 0 ? coord + x : coord + y
    );

    const { minX, minY, width, height } = calculateBounds(absolutePoints);

    // Keep x,y if provided; otherwise derive from bounds
    const finalX = x !== undefined ? x : minX;
    const finalY = y !== undefined ? y : minY;

    // Recompute relative points from finalX, finalY to maintain invariant
    const relativePoints = absolutePoints.map((coord, i) =>
      i % 2 === 0 ? coord - finalX : coord - finalY
    );

    return {
      ...arrow,
      points: relativePoints,
      x: finalX,
      y: finalY,
      width,
      height,
    };
  });

/**
 * Gets the dynamic bounds of the canvas based on all visible elements
 * @param {Object} stageRef - Reference to the Konva stage
 * @returns {Object} Object containing x, y, width, and height of canvas bounds
 */
export const getDynamicCanvasBounds = (stageRef) => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  let hasElements = false;

  // Get bounds from all possible element types
  const allNodes = [
    ...stageRef.current.find("Text"), // Text elements
    // ...stageRef.current.find('Image'),    // Images
    ...stageRef.current.find("Rect"), // Rectangles
    ...stageRef.current.find("Circle"), // Circles
    ...stageRef.current.find("Line"), // Lines and arrows
    ...stageRef.current.find("Path"), // Scribbles
    // ...stageRef.current.find('Group'),    // Grouped elements
  ].filter((node) => {
    // Exclude control points and hidden elements
    const fill = node.fill?.();
    return node.visible() && !["blue", "red"].includes(fill);
  });

  allNodes.forEach((node) => {
    try {
      const rect = node.getClientRect({ relativeTo: stageRef.current });
      if (rect && rect.width > 0 && rect.height > 0) {
        hasElements = true;
        minX = Math.min(minX, rect.x);
        minY = Math.min(minY, rect.y);
        maxX = Math.max(maxX, rect.x + rect.width);
        maxY = Math.max(maxY, rect.y + rect.height);
      }
    } catch (e) {}
  });

  // Fallback to full stage if no elements found
  if (!hasElements || !isFinite(minX)) {
    return {
      x: 0,
      y: 0,
      width: stageRef.current.width(),
      height: stageRef.current.height(),
    };
  }

  // Add padding and ensure positive values
  const padding = 10;
  const result = {
    x: Math.max(0, minX - padding),
    y: Math.max(0, minY - padding),
    width: Math.max(100, maxX - minX + 2 * padding),
    height: Math.max(100, maxY - minY + 2 * padding),
  };

  return result;
};

/**
 * Scale points array to new width and height while maintaining aspect ratio
 * @param {Array<number>} points - Array of coordinates [x1, y1, x2, y2, ...]
 * @param {number} newWidth - New width to scale to
 * @param {number} newHeight - New height to scale to
 * @returns {Array<number>} Scaled points array
 */
export const scalePoints = (points, newWidth, newHeight) => {
  // Find current bounding box
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (let i = 0; i < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minY = Math.min(minY, points[i + 1]);
    maxY = Math.max(maxY, points[i + 1]);
  }
  const currentWidth = maxX - minX;
  const currentHeight = maxY - minY;
  const scaleX = newWidth / (currentWidth || 1);
  const scaleY = newHeight / (currentHeight || 1);

  // Scale points
  const scaledPoints = points.map((val, idx) =>
    idx % 2 === 0 ? minX + (val - minX) * scaleX : minY + (val - minY) * scaleY
  );
  return scaledPoints;
};
